import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Users, Flag, BarChart, Coins, Clock, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function AdminPanel() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pointsInput, setPointsInput] = useState({});
  const [daysInput, setDaysInput] = useState({});

  useEffect(() => {
    fetchStats();
    fetchFlags();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?limit=50`, { withCredentials: true });
      setUsers(response.data.users);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const fetchFlags = async () => {
    try {
      const response = await axios.get(`${API}/admin/feature-flags`, { withCredentials: true });
      setFlags(response.data.flags);
    } catch (error) {
      console.error('Failed to fetch flags:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFlag = async (flagId, currentValue) => {
    try {
      await axios.patch(`${API}/admin/feature-flags/${flagId}`, { value: !currentValue }, { withCredentials: true });
      toast.success(t('admin.flag_updated'));
      fetchFlags();
      window.dispatchEvent(new Event('feature-flags-updated'));
    } catch (error) {
      toast.error(t('admin.flag_update_failed'));
    }
  };

  const updateUserTier = async (userId, newTier) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/tier`, { tier: newTier }, { withCredentials: true });
      toast.success(t('admin.tier_updated'));
      fetchUsers();
    } catch (error) {
      toast.error(t('admin.tier_update_failed'));
    }
  };

  const updateUserPoints = async (userId, action) => {
    const amount = parseInt(pointsInput[userId] || 0);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('admin.invalid_amount'));
      return;
    }
    try {
      const finalAmount = action === 'add' ? amount : -amount;
      await axios.patch(`${API}/admin/users/${userId}/points`, { action: 'add', amount: finalAmount }, { withCredentials: true });
      toast.success(t('admin.points_updated'));
      setPointsInput({ ...pointsInput, [userId]: '' });
      fetchUsers();
    } catch (error) {
      toast.error(t('admin.points_update_failed'));
    }
  };

  const updateUserPremium = async (userId, action) => {
    const days = parseInt(daysInput[userId] || 0);
    if (isNaN(days) || days <= 0) {
      toast.error(t('admin.invalid_days'));
      return;
    }
    try {
      await axios.patch(`${API}/admin/users/${userId}/premium`, { action, days }, { withCredentials: true });
      toast.success(t('admin.premium_updated'));
      setDaysInput({ ...daysInput, [userId]: '' });
      fetchUsers();
    } catch (error) {
      toast.error(t('admin.premium_update_failed'));
    }
  };

  const formatPremiumEnd = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-heading font-bold text-[#2C3E2D] mb-8" data-testid="admin-title">
          {t('admin.title')}
        </h1>

        <div className="flex gap-4 mb-6">
          <Button variant={activeTab === 'stats' ? 'default' : 'outline'} onClick={() => setActiveTab('stats')} data-testid="tab-stats">
            <BarChart className="w-4 h-4 mr-2" />
            {t('admin.statistics')}
          </Button>
          <Button variant={activeTab === 'users' ? 'default' : 'outline'} onClick={() => { setActiveTab('users'); fetchUsers(); }} data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            {t('admin.users')}
          </Button>
          <Button variant={activeTab === 'flags' ? 'default' : 'outline'} onClick={() => setActiveTab('flags')} data-testid="tab-flags">
            <Flag className="w-4 h-4 mr-2" />
            {t('admin.feature_flags')}
          </Button>
        </div>

        {activeTab === 'stats' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6" data-testid="stats-section">
            <Card><CardHeader><CardTitle className="text-sm sm:text-base">{t('admin.total_users')}</CardTitle></CardHeader><CardContent><p className="text-2xl sm:text-4xl font-bold text-primary">{stats.users_total || 0}</p></CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm sm:text-base">{t('admin.households')}</CardTitle></CardHeader><CardContent><p className="text-2xl sm:text-4xl font-bold text-secondary">{stats.households_total || 0}</p></CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm sm:text-base">{t('admin.plans_made')}</CardTitle></CardHeader><CardContent><p className="text-2xl sm:text-4xl font-bold text-success">{stats.plans_total || 0}</p></CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm sm:text-base">{t('admin.recipes_count')}</CardTitle></CardHeader><CardContent><p className="text-2xl sm:text-4xl font-bold text-accent-foreground">{stats.recipes_total || 0}</p></CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm sm:text-base">{t('admin.premium_users')}</CardTitle></CardHeader><CardContent><p className="text-2xl sm:text-4xl font-bold text-primary">{stats.premium_users || 0}</p></CardContent></Card>
            <Card><CardHeader><CardTitle className="text-sm sm:text-base">{t('admin.unlimited_users')}</CardTitle></CardHeader><CardContent><p className="text-2xl sm:text-4xl font-bold text-secondary">{stats.unlimited_users || 0}</p></CardContent></Card>
          </div>
        )}

        {activeTab === 'users' && (
          <Card data-testid="users-section">
            <CardHeader><CardTitle>{t('admin.user_management')}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map((user) => (
                  <div key={user.user_id} className="p-4 bg-gray-50 rounded-lg" data-testid={`user-row-${user.user_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm px-3 py-1 bg-primary/10 text-primary rounded-full capitalize">{user.tier}</span>
                        <select
                          value={user.tier}
                          onChange={(e) => updateUserTier(user.user_id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                          data-testid={`tier-select-${user.user_id}`}
                        >
                          <option value="free">Free</option>
                          <option value="premium">Premium</option>
                          <option value="unlimited">Unlimited</option>
                        </select>
                      </div>
                    </div>

                    {/* Points Management */}
                    <div className="flex items-center gap-3 mb-3 p-3 bg-white rounded border">
                      <Coins className="w-5 h-5 text-yellow-500" />
                      <span className="text-sm font-medium">{t('admin.points')}:</span>
                      <span className="font-bold text-primary">{user.points_balance || 0}</span>
                      <div className="flex items-center gap-2 ml-auto">
                        <Input
                          type="number"
                          min="1"
                          placeholder="0"
                          value={pointsInput[user.user_id] || ''}
                          onChange={(e) => setPointsInput({ ...pointsInput, [user.user_id]: e.target.value })}
                          className="w-20 h-8 text-sm"
                          data-testid={`points-input-${user.user_id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateUserPoints(user.user_id, 'add')}
                          className="h-8"
                          data-testid={`points-add-${user.user_id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateUserPoints(user.user_id, 'subtract')}
                          className="h-8"
                          data-testid={`points-subtract-${user.user_id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Premium Duration Management */}
                    <div className="flex items-center gap-3 p-3 bg-white rounded border">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <span className="text-sm font-medium">{t('admin.premium_until')}:</span>
                      <span className="font-bold text-secondary">{formatPremiumEnd(user.premium_end_at)}</span>
                      <div className="flex items-center gap-2 ml-auto">
                        <Input
                          type="number"
                          min="1"
                          placeholder={t('admin.days')}
                          value={daysInput[user.user_id] || ''}
                          onChange={(e) => setDaysInput({ ...daysInput, [user.user_id]: e.target.value })}
                          className="w-20 h-8 text-sm"
                          data-testid={`days-input-${user.user_id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateUserPremium(user.user_id, 'extend')}
                          className="h-8 text-green-600"
                          data-testid={`premium-extend-${user.user_id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {t('admin.extend')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateUserPremium(user.user_id, 'shorten')}
                          className="h-8 text-red-600"
                          data-testid={`premium-shorten-${user.user_id}`}
                        >
                          <Minus className="w-3 h-3 mr-1" />
                          {t('admin.shorten')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'flags' && (
          <Card data-testid="flags-section">
            <CardHeader><CardTitle>{t('admin.feature_flags')} ({t('admin.toggle_desc')})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {flags.map((flag) => (
                  <div key={flag.flag_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`flag-row-${flag.key}`}>
                    <div className="flex-1">
                      <p className="font-semibold">{flag.key}</p>
                      <p className="text-sm text-gray-600">{flag.description}</p>
                      {flag.value ? (
                        <span className="text-xs text-success mt-1 inline-block">{t('admin.flag_active')}</span>
                      ) : (
                        <span className="text-xs text-gray-500 mt-1 inline-block">{t('admin.flag_disabled')}</span>
                      )}
                    </div>
                    <Switch
                      checked={flag.value}
                      onCheckedChange={() => toggleFlag(flag.flag_id, flag.value)}
                      data-testid={`flag-toggle-${flag.key}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
